let activeCount = 0;
const listeners = new Set();

export class ApiError extends Error {
  constructor(message, { status = 0, details = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

function notify() {
  const loading = activeCount > 0;
  listeners.forEach(listener => listener(loading));
}

function beginLoading(silent) {
  if (silent) return;
  activeCount += 1;
  notify();
}

function endLoading(silent) {
  if (silent) return;
  activeCount = Math.max(0, activeCount - 1);
  notify();
}

async function readResponse(response) {
  if (response.status === 204) return null;
  const type = response.headers.get("content-type") || "";
  if (type.includes("application/json")) return response.json().catch(() => ({}));
  const text = await response.text();
  return text ? { message: text } : {};
}

function errorMessage(data, status) {
  if (status === 401) return "인증이 만료되었습니다.";
  return data?.message || data?.error?.message || `요청을 처리하지 못했습니다. (HTTP ${status})`;
}

export function onLoadingChange(listener) {
  listeners.add(listener);
  listener(activeCount > 0);
  return () => listeners.delete(listener);
}

export async function apiFetch(path, options = {}, token, googleToken) {
  const { silent: silentOption, headers: optionHeaders, ...fetchOptions } = options;
  const method = String(fetchOptions.method || "GET").toUpperCase();
  const silent = silentOption ?? method === "GET";
  const headers = new Headers(optionHeaders || {});
  if (!headers.has("Content-Type") && fetchOptions.body != null) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (googleToken) headers.set("x-google-token", googleToken);

  beginLoading(silent);
  try {
    const response = await fetch(path, { credentials: "same-origin", ...fetchOptions, headers });
    const data = await readResponse(response);
    if (!response.ok) throw new ApiError(errorMessage(data, response.status), { status: response.status, details: data });
    return data;
  } catch (error) {
    if (error instanceof ApiError || error?.name === "AbortError") throw error;
    throw new ApiError("서버에 연결할 수 없습니다.", { details: error });
  } finally {
    endLoading(silent);
  }
}

export async function uploadFile(path, file, token) {
  const form = new FormData();
  form.append("file", file);
  beginLoading(false);
  try {
    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    const data = await readResponse(response);
    if (!response.ok) throw new ApiError(errorMessage(data, response.status), { status: response.status, details: data });
    return data;
  } catch (error) {
    if (error instanceof ApiError || error?.name === "AbortError") throw error;
    throw new ApiError("파일을 업로드할 수 없습니다.", { details: error });
  } finally {
    endLoading(false);
  }
}
